import interpolateComponents from '@automattic/interpolate-components';
import { ReactElement, ReactNode, useCallback } from 'react';
import { sprintf } from 'sprintf-js';

import { ApiNotification } from 'api/notification/types/ApiNotification';
import { Text } from 'components/Text/Text';
import { fontWeights } from 'theme/theme';
import { MappedNotification } from 'types/mappedNotifications/MappedNotification';
import { getListingTypeHumanNameTitle } from 'utils/constants/general/listingTypeHumanNameMapTitle';
import { MappedLocalNotification } from 'utils/localNotifications';

type Props = {
  notification: ApiNotification | MappedNotification | MappedLocalNotification;
  render: (args: {
    to: string;
    eventName: string;
    eventProperties: Record<string, unknown>;
    children: ReactNode;
  }) => ReactElement;
};

export function NotificationListItemContent({ notification, render }: Props) {
  const truncateTitle = useCallback(
    (title: string) =>
      title.length > 70 ? title.slice(0, 70).concat('...') : title,
    [],
  );

  switch (notification.type) {
    case 'NEW_APPLICATIONS_BY_LISTING':
      return render({
        to: `/admin/${
          notification.data.orgId
        }/${notification.data.type.toLowerCase()}s/${notification.data.id}`,
        eventName: 'User Menu Notification',
        eventProperties: {
          notification_type: notification.type,
          listing_id: notification.data.id,
          listing_type: notification.data.type,
          listing_name: notification.data.name,
          org_id: notification.data.orgId,
          application_count: notification.data.applications.length,
        },
        children: (
          <>
            <Text fontWeight={fontWeights.semiBold}>
              {notification.data.name}
            </Text>
            <Text fontWeight={fontWeights.normal}>
              {interpolateComponents({
                mixedString: sprintf(
                  notification.data.count > 1
                    ? getText(
                        '{{strong}}%(count)s{{/strong}} unread applications',
                      )
                    : getText(
                        '{{strong}}%(count)s{{/strong}} unread application',
                      ),
                  {
                    count: notification.data.count,
                  },
                ),
                components: {
                  strong: <strong />,
                },
              })}
            </Text>
          </>
        ),
      });

    case 'MENTION_LISTING_APPLICATION': {
      const title = truncateTitle(notification.data.listingApplication.name);

      return render({
        to: `/admin/${notification.about.id}/applications/${notification.data.listingApplication.id}`,
        eventName: 'User Menu Notification',
        eventProperties: {
          notification_type: notification.type,
          application_id: notification.data.listingApplication.id,
          listing_id: notification.data.listing.id,
          listing_type: notification.data.listing.type,
          listing_name: notification.data.listing.name,
          org_id: notification.about.id,
        },
        children: interpolateComponents({
          mixedString: sprintf(
            getText(
              "{{strong}}%(name)s{{/strong}} mentioned you: {{strong}}%(title)s{{/strong}}'s %(type)s application",
            ),
            {
              name: notification.data.entry.byUser.name,
              title,
              type: getListingTypeHumanNameTitle(
                notification.data.listing,
              ).toLowerCase(),
            },
          ),
          components: {
            strong: <strong />,
          },
        }),
      });
    }

    case 'MENTION_SUGGESTED_APPLICANT': {
      const title = truncateTitle(notification.data.suggestedApplicant.name);

      return render({
        to: `/admin/${notification.about.id}/jobs/${notification.data.listing.id}/recommended-candidates/${notification.data.suggestedApplicant.id}`,
        eventName: 'User Menu Notification',
        eventProperties: {
          notification_type: notification.type,
          suggested_applicant_id: notification.data.suggestedApplicant.id,
          listing_id: notification.data.listing.id,
          listing_type: notification.data.listing.type,
          listing_name: notification.data.listing.name,
          org_id: notification.about.id,
        },
        children: interpolateComponents({
          mixedString: sprintf(
            getText(
              "{{strong}}%(name)s{{/strong}} mentioned you: {{strong}}%(title)s{{/strong}}'s resume",
            ),
            {
              name: notification.data.entry.byUser.name,
              title,
            },
          ),
          components: {
            strong: <strong />,
          },
        }),
      });
    }

    case 'NEW_SUGGESTED_APPLICANTS_BY_JOB': {
      const title = truncateTitle(notification.data.job.name);
      return render({
        to: `/admin/${notification.data.orgId}/jobs/${notification.data.job.id}/recommended-candidates`,
        eventName: 'User Menu Notification',
        eventProperties: {
          notification_type: notification.type,
          listing_id: notification.data.job.id,
          listing_type: 'JOB',
          listing_name: notification.data.job.name,
          org_id: notification.data.orgId,
          candidate_count: notification.data.count,
        },
        children: (
          <>
            <Text fontWeight={fontWeights.semiBold}>{title}</Text>
            <Text fontWeight={fontWeights.normal}>
              {interpolateComponents({
                mixedString: sprintf(
                  getText(
                    '{{strong}}%(count)d{{/strong}} new recommended resumes',
                  ),
                  {
                    count: notification.data.count,
                  },
                ),
                components: {
                  strong: <strong />,
                },
              })}
            </Text>
          </>
        ),
      });
    }

    case 'ORG_ADMIN_USER_DROPDOWN_ATS_CTA': {
      return render({
        to: '/applicant-tracker',
        eventName: 'User Menu Notification',
        eventProperties: {},
        children: (
          <Text fontWeight={fontWeights.normal}>
            {interpolateComponents({
              mixedString: sprintf(
                getText(
                  'Try our applicant management tool, included with any listing. {{strong}}Learn More{{/strong}}',
                ),
              ),
              components: {
                strong: <strong />,
              },
            })}
          </Text>
        ),
      });
    }

    case 'SALARY_SURVEY_CTA': {
      return render({
        to: '/nonprofit-salary-explorer',
        eventName: 'User Menu Notification',
        eventProperties: {
          cta_type: 'Link',
          button_text: 'Take Our Salary Survey',
          url: '/nonprofit-salary-explorer',
          context: 'User Menu',
        },
        children: (
          <Text fontWeight={fontWeights.normal}>
            {getText('Take Our Salary Survey')}
          </Text>
        ),
      });
    }

    default:
      return null;
  }
}
